/**
 * NCEMI-specific form data builders.
 *
 * Builds guardrails and attributes for NO_COST_EMI campaign type
 * from form state. Extracted from formDataBuilder for single-responsibility.
 *
 * @module ncemiDataBuilder
 */

import { CampaignConfigurationData } from 'src/@types/valueMatching/campaignConfiguration';
import {
    FundingShareType,
    PaymentInstrumentType,
} from 'src/@types/valueMatching/enums';
import { NoCostEMIGuardrails, PaymentInstrumentBasedBankTenureCombination } from 'src/@types/valueMatching/ncemi';
import { NoCostEMIAttributes } from 'src/@types/valueMatching/attributes';
import { UseCampaignFormResult } from 'src/hooks/useCampaignForm/useCampaignForm';
import { parseOptionalNumber } from 'src/utils/helpers';

// ============================================================================
// Public API
// ============================================================================

/** Builds NCEMI guardrails from form state. */
export function buildNcemiGuardrails(form: UseCampaignFormResult): NoCostEMIGuardrails {
    return {
        amazonPriceLeadingGuardrail: parseOptionalNumber(form.priceLeadingGuardrail),
        paymentInstrumentBankTenureGuardrail: {
            paymentInstrumentBasedBankTenureCombinations: buildBankTenureCombos(form),
        },
    };
}

/** Builds NCEMI attributes from form state. */
export function buildNcemiAttributes(
    form: UseCampaignFormResult,
    config: CampaignConfigurationData | null,
): NoCostEMIAttributes {
    const attrs: NoCostEMIAttributes = {
        killOverCompetitiveNCEOffers: false,
        fundingShareByFundingShareType: {
            [FundingShareType.SELLER]: form.sellerShare ?? 0,
            [FundingShareType.BRAND]: form.brandShare ?? 0,
            [FundingShareType.BANK]: form.bankShare ?? 0,
            [FundingShareType.AMAZON]: form.amazonShare ?? 0,
        },
    };

    // Brand attributes — look up OFA ID from config mapping
    const brandMapping = config?.campaignTypeConfiguration?.noCostEMI?.brandToOfaMapping;
    if (form.brandName && brandMapping && brandMapping[form.brandName]) {
        attrs.brandAttributes = {
            brandName: form.brandName,
            brandOFAId: brandMapping[form.brandName],
        };
    }

    return attrs;
}

// ============================================================================
// Internal helpers
// ============================================================================

/* eslint-disable no-secrets/no-secrets */
/**
 * Builds NCEMI bank tenure combinations from form card selections.
 *
 * For each card type (credit, debit, CBCC, BFL), if any banks are selected,
 * creates a `PaymentInstrumentBasedBankTenureCombination` with each bank
 * mapped to tenures from 3 up to the selected max tenure.
 */
/* eslint-enable no-secrets/no-secrets */
function buildBankTenureCombos(form: UseCampaignFormResult): PaymentInstrumentBasedBankTenureCombination[] {
    const combos: PaymentInstrumentBasedBankTenureCombination[] = [];

    const cardGroups: Array<{
        instrumentType: PaymentInstrumentType;
        selectedBanks: string[];
        maxTenure: string;
    }> = [
            {
                instrumentType: PaymentInstrumentType.CREDIT_CARD,
                selectedBanks: form.selectedCreditCards,
                maxTenure: form.creditCardTenure,
            },
            {
                instrumentType: PaymentInstrumentType.DEBIT_CARD,
                selectedBanks: form.selectedDebitCards,
                maxTenure: form.debitCardTenure,
            },
            {
                instrumentType: PaymentInstrumentType.FLIPKART_CARD,
                selectedBanks: form.selectedCbccCards,
                maxTenure: form.cbccCardTenure,
            },
            {
                instrumentType: PaymentInstrumentType.BFL_CARD,
                selectedBanks: form.selectedBflCards,
                maxTenure: form.bflCardTenure,
            },
        ];

    for (const group of cardGroups) {
        if (group.selectedBanks.length === 0) continue;

        const maxTenureMonths = parseInt(group.maxTenure, 10) || 0;
        const tenures = buildTenureArray(maxTenureMonths);

        // Skip card types where no valid tenure could be built (e.g., maxMonths < 3).
        // Sending empty tenures to the API is invalid; better to omit the card type entirely.
        if (tenures.length === 0) continue;

        combos.push({
            paymentInstrumentType: group.instrumentType,
            bankTenureCombinations: group.selectedBanks.map((bankName) => ({
                bankName,
                tenures,
            })),
        });
    }

    return combos;
}

/**
 * Builds an array of tenure months from 3 up to maxMonths in increments of 3.
 * E.g., maxMonths=12 → [3, 6, 9, 12]
 */
function buildTenureArray(maxMonths: number): number[] {
    const tenures: number[] = [];
    for (let m = 3; m <= maxMonths; m += 3) {
        tenures.push(m);
    }
    return tenures;
}
